import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  Chip,
  Alert,
  AlertTitle,
  Checkbox,
  FormControlLabel,
  TextField,
  Tooltip,
  CircularProgress,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  Link as LinkIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  HelpOutline as HelpOutlineIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  SwapHoriz as SwapHorizIcon,
  DeleteOutline as DeleteOutlineIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import { useReadOnlyMode } from '../../hooks/useReadOnlyMode';
import { ReadOnlyButton } from '../common/ReadOnlyButton';
import { PortalLoading, PortalErrorState, PortalEmptyState } from '../portal/PortalStates';
import {
  CK,
  FONT_TITLE,
  FONT_BODY,
  statusChipSx,
  sectionCardSx,
  insetPanelSx,
  sectionHeadingSx,
  emptyTextSx,
  splitRowSx,
  actionRowSx,
  dialogProps,
  dialogTitleSx,
  statTileSx,
} from '../../pages/portal/portalStyles';
import { pageVariants, sectionVariants } from '../../animations/variants';

// PolicyReferenceDetail renders one CUSTOMER-owned policy reference: what it
// actually resolves to in every account, and the one-way conversion that turns
// it into a Prism-owned policy.
//
// The screen exists because a reference is NOT one document. The same name can
// resolve to a different document in every account, and the divergence is the
// information an operator needs — so this component never collapses the
// variants into a single "the policy", and never folds "the customer never
// created it here" (MISSING) into "Prism was denied and does not know"
// (UNREADABLE). Those two send an admin to fix completely different problems.

// ---------------------------------------------------------------------------
// per-account states — one table, so a state is worded and coloured the same
// wherever it appears
// ---------------------------------------------------------------------------
//
// The variant breakdown and the conversion preview describe the SAME accounts
// through two vocabularies (`FOUND`/`MISSING`/`UNREADABLE` vs
// `unchanged`/`changed`/`newly_created`/`unknown`). They are keyed to a shared
// intent here so an account that is amber in one section cannot be red in the
// other and read as a different account.
const STATE = {
  agrees: { intent: 'success', label: 'Same document' },
  differs: { intent: 'error', label: 'Different document' },
  missing: { intent: 'warning', label: 'Not present' },
  unreadable: { intent: 'purple', label: 'Could not read' },
};

// The four conversion-impact buckets, in the order they are shown. `changed`
// is first and is the only bucket rendered as its own banner: those are the
// accounts whose real permissions move.
const IMPACT_BUCKETS = [
  {
    key: 'changed',
    state: STATE.differs,
    icon: <WarningAmberIcon sx={{ fontSize: 18 }} />,
    title: 'Permissions change',
    blurb: 'These accounts hold a different document today. Converting rewrites what they grant.',
  },
  {
    key: 'unchanged',
    state: STATE.agrees,
    icon: <CheckCircleIcon sx={{ fontSize: 18 }} />,
    title: 'No change',
    blurb: 'These accounts already hold exactly the document Prism would own.',
  },
  {
    key: 'newly_created',
    state: STATE.missing,
    icon: <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />,
    title: 'Created for the first time',
    blurb: 'The policy does not exist in these accounts today; conversion creates it there.',
  },
  {
    key: 'unknown',
    state: STATE.unreadable,
    icon: <HelpOutlineIcon sx={{ fontSize: 18 }} />,
    title: 'Unknown',
    blurb: 'Prism could not read these accounts, so their effect cannot be predicted.',
  },
];

const PRISM_OWNED_PREFIX = 'Prism-';

// ---------------------------------------------------------------------------
// small helpers
// ---------------------------------------------------------------------------

// formatDocument pretty-prints a policy document, falling back to the raw text.
// A document Prism could not parse is still shown verbatim rather than hidden —
// the operator has to be able to read whatever is actually in the account.
const formatDocument = (doc) => {
  if (!doc) return '';
  try {
    return JSON.stringify(JSON.parse(doc), null, 2);
  } catch {
    return doc;
  }
};

const shortFingerprint = (fp) => (fp ? `${String(fp).slice(0, 12)}…` : '—');

// Go marshals a never-set time.Time as year 0001; rendering that as a date
// would claim the sweep happened when it never did.
const isRealTimestamp = (ts) => !!ts && !String(ts).startsWith('0001-01-01');

const formatWhen = (ts) => (isRealTimestamp(ts) ? new Date(ts).toLocaleString() : null);

// lastCheckedAt is the newest per-account observation. The sweep writes every
// account in one pass, so the maximum is when the data on screen was gathered.
const lastCheckedAt = (accounts = []) => {
  let newest = null;
  accounts.forEach((a) => {
    if (!isRealTimestamp(a?.checked_at)) return;
    const t = new Date(a.checked_at).getTime();
    if (Number.isNaN(t)) return;
    if (newest === null || t > newest) newest = t;
  });
  return newest === null ? null : new Date(newest).toISOString();
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// The shared api layer throws a plain Error carrying the server's message and
// drops the status code, so a 409 has to be recognised from whatever the error
// actually carries. Both are checked: the status if some caller attaches one,
// and the backend's own acknowledgement wording otherwise. Guessing wrong here
// only costs a less specific message — it never lets a conversion through.
const statusOf = (err) =>
  err?.status || err?.statusCode || err?.response?.status || err?.data?.status || null;

const isAcknowledgementConflict = (err) => {
  if (err?.requires_acknowledgement || err?.data?.requires_acknowledgement) return true;
  if (/acknowledg/i.test(err?.message || '')) return true;
  // The status alone is not enough: 409 is ALSO how the backend reports "a
  // Prism-owned policy already has that name", and telling that operator to
  // re-acknowledge would send them to the wrong fix. Only fall back to the
  // status when the message identifies nothing more specific.
  return statusOf(err) === 409 && !/already exists/i.test(err?.message || '');
};

const documentBlockSx = {
  ...insetPanelSx,
  m: 0,
  fontFamily: 'monospace',
  fontSize: '0.78rem',
  lineHeight: 1.6,
  color: CK.navy,
  whiteSpace: 'pre',
  overflowX: 'auto',
  overflowY: 'auto',
  maxHeight: 320,
};

// AccountChips renders a set of account IDs. Never truncated to a count: the
// point of this screen is which accounts diverge, and "and 14 more" is exactly
// the information an operator came here for.
function AccountChips({ ids = [], intent = 'neutral', emptyLabel = 'None' }) {
  if (!ids.length) {
    return <Typography sx={{ ...emptyTextSx, fontSize: '0.8rem' }}>{emptyLabel}</Typography>;
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.75,
        maxHeight: 132,
        overflowY: 'auto',
      }}
    >
      {ids.map((id) => (
        <Chip
          key={id}
          size="small"
          label={id}
          sx={{ ...statusChipSx(intent), fontFamily: 'monospace', fontWeight: 500 }}
        />
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// component
// ---------------------------------------------------------------------------

export function PolicyReferenceDetail({
  customerId,
  referenceId,
  onClose,
  onConverted,
  onDeleted,
}) {
  const isReadOnly = useReadOnlyMode();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sweeping, setSweeping] = useState(false);
  const [sweepError, setSweepError] = useState(null);

  const [convertOpen, setConvertOpen] = useState(false);
  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactError, setImpactError] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState(null);
  const [ackConflict, setAckConflict] = useState(null);

  // Set once a conversion succeeds. The reference row is gone at that point, so
  // the breakdown below it describes something that no longer exists — the
  // screen has to say so rather than keep rendering stale variants next to a
  // live "Convert" button.
  const [converted, setConverted] = useState(null);

  const [removeOpen, setRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  // Synchronous double-submit latch. `converting` state alone cannot stop two
  // clicks landing in the same tick (React batches, so the disabled prop has
  // not rendered yet), and a double conversion is not recoverable — the first
  // call consumes the reference and the second would either fail loudly or,
  // worse, race the first. The ref flips before any await.
  const convertingRef = useRef(false);

  const load = useCallback(async () => {
    if (!referenceId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getPolicyReference(customerId, referenceId);
      setDetail(res || null);
    } catch (e) {
      setError(e?.message || 'Failed to load this policy reference');
    } finally {
      setLoading(false);
    }
  }, [customerId, referenceId]);

  useEffect(() => {
    load();
  }, [load]);

  const runSweep = useCallback(async () => {
    if (sweeping) return;
    setSweeping(true);
    setSweepError(null);
    try {
      // The sweep endpoint returns the refreshed detail, so there is no second
      // round trip and no window where the screen shows pre-sweep data next to
      // a "just checked" timestamp.
      const res = await apiService.sweepPolicyReference(customerId, referenceId);
      if (res) setDetail(res);
      else await load();
    } catch (e) {
      setSweepError(e?.message || 'Re-check failed');
    } finally {
      setSweeping(false);
    }
  }, [customerId, referenceId, sweeping, load]);

  const groups = useMemo(() => detail?.variant_groups || [], [detail]);
  const accounts = useMemo(() => detail?.accounts || [], [detail]);
  const missing = useMemo(() => detail?.missing_from_accounts || [], [detail]);
  const unreadable = useMemo(() => detail?.unreadable_in_accounts || [], [detail]);
  const attachedTo = useMemo(() => detail?.attached_to_permission_set_ids || [], [detail]);
  const checkedCount = detail?.accounts_checked || 0;
  const sweptAt = useMemo(() => lastCheckedAt(accounts), [accounts]);
  const neverSwept = checkedCount === 0;

  // Why an account could not be read, keyed by account. Without the reason the
  // "unreadable" list is unactionable — the fix is usually a missing trust or
  // a denied GetPolicy, and only the message says which.
  const errorByAccount = useMemo(() => {
    const map = {};
    accounts.forEach((a) => {
      if (a?.error_message) map[a.account_id] = a.error_message;
    });
    return map;
  }, [accounts]);

  const inAgreement =
    groups.length === 1 && missing.length === 0 && unreadable.length === 0 && checkedCount > 0;

  const policyName = detail?.policy_name || '';
  const policyPath = detail?.policy_path || '/';
  const ownedName = `${PRISM_OWNED_PREFIX}${policyName}`;

  // -------------------------------------------------------------------------
  // convert flow
  // -------------------------------------------------------------------------

  const openConvert = useCallback(async () => {
    // Every guard resets here rather than on close: a dialog reopened after a
    // cancelled attempt must never come back with the acknowledgement still
    // ticked from last time.
    setConvertOpen(true);
    setImpact(null);
    setImpactError(null);
    setConvertError(null);
    setAckConflict(null);
    setAcknowledged(false);
    setTypedName('');
    setImpactLoading(true);
    try {
      const res = await apiService.getPolicyReferenceConversionImpact(customerId, referenceId);
      setImpact(res || null);
    } catch (e) {
      setImpactError(e?.message || 'Could not work out what conversion would do');
    } finally {
      setImpactLoading(false);
    }
  }, [customerId, referenceId]);

  const closeConvert = useCallback(() => {
    if (convertingRef.current) return; // never close mid-flight
    setConvertOpen(false);
  }, []);

  const impactBuckets = impact?.impact || {};
  const changed = impactBuckets.changed || [];
  const unchanged = impactBuckets.unchanged || [];
  const newlyCreated = impactBuckets.newly_created || [];
  const unknown = impactBuckets.unknown || [];
  const proposedDocument = impact?.proposed_document || '';
  const impactAttached = impact?.attached_to_permission_set_ids || attachedTo;

  // A conversion is only offerable against observed data. `swept === false`
  // means no account has ever been read, and an empty impact would then render
  // "0 accounts change" as reassurance for a fleet nobody has looked at.
  const previewUsable = !!impact && impact.swept !== false && !!proposedDocument;

  // Both gestures are required, not either: the checkbox states the blast
  // radius in numbers, the typed name proves the operator is on the policy they
  // think they are on. A single click can satisfy neither.
  const confirmationComplete = acknowledged && typedName.trim() === policyName;
  const canSubmitConversion = previewUsable && confirmationComplete && !converting && !isReadOnly;

  const submitConversion = useCallback(async () => {
    if (convertingRef.current) return;
    if (!previewUsable || !confirmationComplete) return;
    convertingRef.current = true;
    setConverting(true);
    setConvertError(null);
    setAckConflict(null);
    try {
      const res = await apiService.convertPolicyReference(customerId, referenceId, {
        document: proposedDocument,
        // Mirrors the operator's actual gesture rather than a hardcoded true,
        // so the server's acknowledgement gate stays a real check.
        acknowledged: confirmationComplete,
      });
      setConvertOpen(false);
      setConverted(res || {});
      if (onConverted) onConverted(res);
    } catch (e) {
      if (isAcknowledgementConflict(e)) {
        // 409: the server refused because it did not see an acknowledgement,
        // and — this is the part that has to be said — NOTHING was converted.
        // The tick is cleared so the operator must consciously re-confirm.
        setAckConflict(
          e?.message ||
            'Prism did not receive the acknowledgement, so nothing was converted.',
        );
        setAcknowledged(false);
      } else {
        setConvertError(e?.message || 'Conversion failed');
      }
    } finally {
      convertingRef.current = false;
      setConverting(false);
    }
  }, [
    customerId,
    referenceId,
    proposedDocument,
    previewUsable,
    confirmationComplete,
    onConverted,
  ]);

  // -------------------------------------------------------------------------
  // remove (detach only)
  // -------------------------------------------------------------------------

  const submitRemove = useCallback(async () => {
    if (removing) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await apiService.deletePolicyReference(customerId, referenceId);
      setRemoveOpen(false);
      if (onDeleted) onDeleted(referenceId);
    } catch (e) {
      setRemoveError(e?.message || 'Could not remove the reference');
    } finally {
      setRemoving(false);
    }
  }, [customerId, referenceId, removing, onDeleted]);

  // -------------------------------------------------------------------------
  // render
  // -------------------------------------------------------------------------

  if (loading && !detail) {
    return (
      <Paper elevation={0} sx={sectionCardSx}>
        <PortalLoading label="Loading policy reference…" color={CK.purple} />
      </Paper>
    );
  }

  if (error && !detail) {
    return <PortalErrorState error={error} onRetry={load} />;
  }

  if (!detail) return null;

  // Post-conversion. The reference is gone, so its variant breakdown is history
  // and every action on it would 404 — this replaces the screen rather than
  // leaving a live-looking one behind.
  if (converted) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <Paper elevation={0} sx={sectionCardSx}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Avatar sx={{ backgroundColor: `${CK.green}14`, color: CK.green, width: 48, height: 48 }}>
              <CheckCircleIcon />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...sectionHeadingSx, fontSize: '1.25rem' }}>
                {policyName} is now a Prism-owned policy
              </Typography>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mt: 1 }}>
                Prism owns <strong>{converted.name || policyName}</strong> and materialises it as{' '}
                <strong>{ownedName}</strong> in each account. The customer&apos;s original{' '}
                <strong>{policyPath}{policyName}</strong> is still there, untouched — Prism has
                simply stopped attaching it. The reference no longer exists in Prism.
              </Typography>
              {onClose && (
                <Button
                  onClick={onClose}
                  variant="outlined"
                  sx={{
                    mt: 2.5,
                    textTransform: 'none',
                    fontFamily: FONT_BODY,
                    fontWeight: 600,
                    borderColor: CK.border,
                    color: CK.navy,
                  }}
                >
                  Done
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </motion.div>
    );
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      {/* ---------------------------------------------------------------- */}
      {/* header                                                            */}
      {/* ---------------------------------------------------------------- */}
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
          <Box sx={splitRowSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
              <Avatar sx={{ backgroundColor: `${CK.purple}14`, color: CK.purple, width: 48, height: 48 }}>
                <LinkIcon />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: FONT_TITLE,
                    fontWeight: 600,
                    fontSize: '1.5rem',
                    color: CK.navy,
                    wordBreak: 'break-all',
                  }}
                >
                  {policyName}
                </Typography>
                <Typography
                  sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: CK.muted }}
                >
                  {policyPath}
                  {policyName}
                </Typography>
              </Box>
            </Box>

            <Box sx={actionRowSx}>
              <Tooltip
                title={
                  sweptAt
                    ? `Re-reads this policy in the accounts where a permission set using it is provisioned. Last checked ${formatWhen(sweptAt)}.`
                    : 'Re-reads this policy in the accounts where a permission set using it is provisioned.'
                }
              >
                <span>
                  <ReadOnlyButton
                    variant="outlined"
                    startIcon={
                      sweeping ? <CircularProgress size={16} sx={{ color: CK.purple }} /> : <RefreshIcon />
                    }
                    onClick={runSweep}
                    disabled={sweeping}
                    sx={{
                      textTransform: 'none',
                      fontFamily: FONT_BODY,
                      fontWeight: 600,
                      borderColor: CK.border,
                      color: CK.navy,
                      '&:hover': { borderColor: CK.purple, color: CK.purple },
                    }}
                  >
                    {sweeping ? 'Checking…' : 'Re-check accounts'}
                  </ReadOnlyButton>
                </span>
              </Tooltip>

              <Tooltip
                title={
                  neverSwept
                    ? 'Run a check first — Prism has not read this policy in any account yet, so it cannot show you what conversion would change.'
                    : 'One-way: Prism takes ownership of a copy of this policy.'
                }
              >
                <span>
                  <ReadOnlyButton
                    variant="contained"
                    startIcon={<SwapHorizIcon />}
                    onClick={openConvert}
                    disabled={neverSwept || sweeping}
                    sx={{
                      textTransform: 'none',
                      fontFamily: FONT_BODY,
                      fontWeight: 600,
                      backgroundColor: CK.purple,
                      '&:hover': { backgroundColor: '#7D3C98' },
                    }}
                  >
                    Convert to Prism-owned
                  </ReadOnlyButton>
                </span>
              </Tooltip>

              <Tooltip title="Removes Prism's pointer only. The customer's IAM policy is never deleted.">
                <span>
                  <ReadOnlyButton
                    variant="text"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => {
                      setRemoveError(null);
                      setRemoveOpen(true);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontFamily: FONT_BODY,
                      fontWeight: 600,
                      color: CK.body,
                      '&:hover': { color: CK.red },
                    }}
                  >
                    Remove reference
                  </ReadOnlyButton>
                </span>
              </Tooltip>

              {onClose && (
                <IconButton onClick={onClose} sx={{ color: CK.body }} aria-label="Close">
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2.5 }}>
            <Chip size="small" label="Customer-owned" sx={statusChipSx('purple')} />
            <Chip
              size="small"
              label={`${plural(attachedTo.length, 'permission set', 'permission sets')}`}
              sx={statusChipSx('info')}
            />
            <Chip
              size="small"
              label={`${plural(checkedCount, 'account checked', 'accounts checked')}`}
              sx={statusChipSx('neutral')}
            />
            {sweptAt && (
              <Chip size="small" label={`Last checked ${formatWhen(sweptAt)}`} sx={statusChipSx('neutral')} />
            )}
          </Box>

          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.85rem', color: CK.body, mt: 2 }}>
            Prism attaches this policy at its real ARN in each account. It never copies, edits or
            deletes the customer&apos;s document — which is why the same name can legitimately
            resolve to different permissions in different accounts.
          </Typography>
        </Paper>
      </motion.div>

      {sweepError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSweepError(null)}>
          <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 600 }}>Re-check failed</AlertTitle>
          {sweepError}
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', mt: 0.5 }}>
            The breakdown below is still from the previous check
            {sweptAt ? ` (${formatWhen(sweptAt)})` : ''}.
          </Typography>
        </Alert>
      )}

      {error && detail && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* variant breakdown                                                 */}
      {/* ---------------------------------------------------------------- */}
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
          <Box sx={{ ...splitRowSx, mb: 2 }}>
            <Box>
              <Typography sx={sectionHeadingSx}>What this resolves to</Typography>
              <Typography sx={{ ...emptyTextSx, fontSize: '0.8rem', mt: 0.5 }}>
                {neverSwept
                  ? 'Not checked yet'
                  : `${plural(groups.length, 'distinct document', 'distinct documents')} across ${plural(
                      checkedCount,
                      'account',
                      'accounts',
                    )}`}
              </Typography>
            </Box>
          </Box>

          {neverSwept ? (
            <PortalEmptyState
              icon={<RefreshIcon />}
              color={CK.purple}
              title="Never checked"
              description="Prism has not yet read this policy in any account, so there is nothing to compare. Run a check to see what each account actually holds."
              action={
                <ReadOnlyButton
                  variant="contained"
                  onClick={runSweep}
                  disabled={sweeping}
                  startIcon={sweeping ? <CircularProgress size={16} sx={{ color: '#FFF' }} /> : <RefreshIcon />}
                  sx={{
                    textTransform: 'none',
                    fontFamily: FONT_BODY,
                    fontWeight: 600,
                    backgroundColor: CK.purple,
                    '&:hover': { backgroundColor: '#7D3C98' },
                  }}
                >
                  Check accounts now
                </ReadOnlyButton>
              }
            />
          ) : (
            <>
              {inAgreement && (
                <Alert
                  icon={<CheckCircleIcon fontSize="inherit" />}
                  severity="success"
                  sx={{ mb: 2.5, fontFamily: FONT_BODY }}
                >
                  <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 600 }}>
                    All {checkedCount} {checkedCount === 1 ? 'account agrees' : 'accounts agree'}
                  </AlertTitle>
                  Every account Prism checked holds an identical document, and none is missing or
                  unreadable. Nothing here needs attention.
                </Alert>
              )}

              {groups.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2.5, fontFamily: FONT_BODY }}>
                  <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 600 }}>
                    No account holds this policy
                  </AlertTitle>
                  Prism found no readable copy of <strong>{policyName}</strong> anywhere. A
                  permission set carrying this reference grants nothing in the accounts below.
                </Alert>
              )}

              {groups.map((group, index) => {
                const isPlurality = index === 0;
                const only = groups.length === 1;
                return (
                  <Box
                    key={group.fingerprint || index}
                    sx={{
                      border: `1px solid ${isPlurality && !only ? `${CK.green}55` : CK.border}`,
                      borderRadius: '8px',
                      p: 2.5,
                      mb: 2,
                    }}
                  >
                    <Box sx={{ ...splitRowSx, mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography
                          sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: CK.navy, fontSize: '0.95rem' }}
                        >
                          {only ? 'The document in use' : `Variant ${index + 1}`}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${plural(group.account_count || (group.account_ids || []).length, 'account', 'accounts')}`}
                          sx={statusChipSx(isPlurality ? 'success' : 'error')}
                        />
                        {isPlurality && !only && (
                          <Tooltip title="Held by the most accounts. Conversion would adopt this document.">
                            <Chip size="small" label="Plurality" sx={statusChipSx('success')} />
                          </Tooltip>
                        )}
                        {!isPlurality && (
                          <Chip size="small" label={STATE.differs.label} sx={statusChipSx('error')} />
                        )}
                      </Box>
                      <Tooltip title={group.fingerprint || ''}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: CK.muted }}>
                          {shortFingerprint(group.fingerprint)}
                        </Typography>
                      </Tooltip>
                    </Box>

                    <AccountChips
                      ids={group.account_ids || []}
                      intent={isPlurality ? 'success' : 'error'}
                    />

                    <Box sx={{ mt: 2 }}>
                      <Box component="pre" sx={documentBlockSx}>
                        {formatDocument(group.document)}
                      </Box>
                    </Box>
                  </Box>
                );
              })}

              {/* MISSING and UNREADABLE stay in separate blocks with separate
                  wording. They look similar on a dashboard and are not: one is
                  a policy the customer never created, the other is a policy
                  Prism was not allowed to read. */}
              {missing.length > 0 && (
                <Box sx={{ border: `1px solid ${CK.amber}55`, borderRadius: '8px', p: 2.5, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <RemoveCircleOutlineIcon sx={{ fontSize: 18, color: CK.amber }} />
                    <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: CK.navy }}>
                      Not present in {plural(missing.length, 'account', 'accounts')}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.82rem', color: CK.body, mb: 1.5 }}>
                    The customer has never created <strong>{policyName}</strong> in these accounts.
                    A permission set carrying this reference grants nothing there. Prism read them
                    successfully — the policy simply is not there.
                  </Typography>
                  <AccountChips ids={missing} intent={STATE.missing.intent} />
                </Box>
              )}

              {unreadable.length > 0 && (
                <Box sx={{ border: `1px solid ${CK.purple}55`, borderRadius: '8px', p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <HelpOutlineIcon sx={{ fontSize: 18, color: CK.purple }} />
                    <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: CK.navy }}>
                      Could not be read in {plural(unreadable.length, 'account', 'accounts')}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.82rem', color: CK.body, mb: 1.5 }}>
                    Prism was denied when it tried to read these accounts. This is <strong>not</strong>{' '}
                    the same as the policy being absent — the document may well exist and differ.
                    Fix access, then re-check.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {unreadable.map((id) => (
                      <Box key={id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          label={id}
                          sx={{ ...statusChipSx('purple'), fontFamily: 'monospace', fontWeight: 500 }}
                        />
                        <Typography
                          sx={{ fontFamily: FONT_BODY, fontSize: '0.78rem', color: CK.muted, flex: 1, minWidth: 200 }}
                        >
                          {errorByAccount[id] || 'No error detail was recorded.'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Paper>
      </motion.div>

      {/* ---------------------------------------------------------------- */}
      {/* convert dialog                                                    */}
      {/* ---------------------------------------------------------------- */}
      <Dialog
        open={convertOpen}
        onClose={closeConvert}
        {...dialogProps}
        maxWidth="md"
        // A mid-flight backdrop/escape close would hide an in-progress
        // irreversible write behind a closed dialog.
        disableEscapeKeyDown={converting}
      >
        <DialogTitle sx={dialogTitleSx}>Convert to a Prism-owned policy</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {impactLoading ? (
            <PortalLoading label="Working out what would change…" color={CK.purple} />
          ) : impactError ? (
            <Alert severity="error" sx={{ fontFamily: FONT_BODY }}>
              <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 600 }}>
                Could not preview the conversion
              </AlertTitle>
              {impactError}
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', mt: 1 }}>
                Nothing has been changed. Conversion stays blocked until Prism can show you its
                effect.
              </Typography>
            </Alert>
          ) : impact && impact.swept === false ? (
            <Alert severity="warning" sx={{ fontFamily: FONT_BODY }}>
              <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 600 }}>
                Not checked yet — conversion is blocked
              </AlertTitle>
              Prism has never read this policy in any account, so it cannot tell you which accounts
              would change. Run <strong>Re-check accounts</strong> first.
            </Alert>
          ) : !proposedDocument ? (
            <Alert severity="warning" sx={{ fontFamily: FONT_BODY }}>
              <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 600 }}>
                No document to adopt
              </AlertTitle>
              Prism found no readable copy of this policy in any account, so there is no document it
              could take ownership of. Fix access or create the policy, then re-check.
            </Alert>
          ) : (
            <>
              {/* The blast radius, first and largest. Everything else on this
                  dialog is context for this number. */}
              <Alert
                severity={changed.length > 0 ? 'error' : 'success'}
                icon={changed.length > 0 ? <ErrorOutlineIcon fontSize="inherit" /> : undefined}
                sx={{ fontFamily: FONT_BODY, mb: 2.5 }}
              >
                <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: '1rem' }}>
                  {changed.length > 0
                    ? `${plural(changed.length, 'account', 'accounts')} will gain or lose permissions`
                    : 'No account loses or gains permissions from a document change'}
                </AlertTitle>
                {changed.length > 0 ? (
                  <>
                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.85rem', mb: 1.5 }}>
                      These accounts hold a <strong>different</strong> document today. Converting
                      replaces what they grant with the document below — permissions really change
                      in these accounts.
                    </Typography>
                    <AccountChips ids={changed} intent="error" />
                  </>
                ) : (
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.85rem' }}>
                    Every account Prism could read already holds exactly this document.
                  </Typography>
                )}
              </Alert>

              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                {IMPACT_BUCKETS.map((bucket) => {
                  const ids =
                    bucket.key === 'changed'
                      ? changed
                      : bucket.key === 'unchanged'
                        ? unchanged
                        : bucket.key === 'newly_created'
                          ? newlyCreated
                          : unknown;
                  return (
                    <Grid size={{ xs: 12, sm: 6 }} key={bucket.key}>
                      <Paper elevation={0} sx={{ ...statTileSx, p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip
                            size="small"
                            icon={bucket.icon}
                            label={ids.length}
                            sx={statusChipSx(bucket.state.intent)}
                          />
                          <Typography
                            sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: CK.navy, fontSize: '0.9rem' }}
                          >
                            {bucket.title}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.78rem', color: CK.body, mb: 1 }}>
                          {bucket.blurb}
                        </Typography>
                        <Box sx={{ mt: 'auto' }}>
                          <AccountChips ids={ids} intent={bucket.state.intent} emptyLabel="No accounts" />
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>

              {unknown.length > 0 && (
                <Alert severity="warning" sx={{ fontFamily: FONT_BODY, mb: 2.5 }}>
                  Prism could not read {plural(unknown.length, 'account', 'accounts')}. Their effect
                  is genuinely unknown, not &ldquo;unchanged&rdquo; — converting may change
                  permissions there without this preview having shown it.
                </Alert>
              )}

              <Alert severity="info" sx={{ fontFamily: FONT_BODY, mb: 2.5 }}>
                <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 600 }}>
                  This is one-way, and the original is left alone
                </AlertTitle>
                <Typography component="div" sx={{ fontFamily: FONT_BODY, fontSize: '0.85rem' }}>
                  <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                    <li>
                      Prism creates and owns <strong>{ownedName}</strong> in every account, built
                      from the single document below.
                    </li>
                    <li>
                      The customer&apos;s own <strong>{policyPath}{policyName}</strong> is{' '}
                      <strong>left in place, untouched</strong> — Prism just stops attaching it.
                    </li>
                    <li>
                      {plural(impactAttached.length, 'permission set', 'permission sets')} switch
                      over to the Prism-owned copy.
                    </li>
                    <li>
                      There is <strong>no revert</strong>. Reverting would change permissions a
                      second time, so Prism does not offer one.
                    </li>
                  </Box>
                </Typography>
              </Alert>

              <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: CK.navy, mb: 1 }}>
                The document Prism would own
                {impact?.proposed_fingerprint ? ` (${shortFingerprint(impact.proposed_fingerprint)})` : ''}
              </Typography>
              <Box component="pre" sx={{ ...documentBlockSx, mb: 1 }}>
                {formatDocument(proposedDocument)}
              </Box>
              {sweptAt && (
                <Typography sx={{ ...emptyTextSx, fontSize: '0.75rem', mb: 2.5 }}>
                  Based on the check that ran {formatWhen(sweptAt)}. If accounts have changed since,
                  cancel and re-check first.
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              {ackConflict && (
                <Alert severity="warning" sx={{ fontFamily: FONT_BODY, mb: 2 }}>
                  <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 600 }}>
                    Nothing was converted — acknowledgement missing
                  </AlertTitle>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.85rem' }}>
                    Prism refused the request because it did not carry your acknowledgement, so{' '}
                    <strong>no account and no permission set was touched</strong>. Tick the box
                    again to confirm and retry.
                  </Typography>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.78rem', color: CK.body, mt: 1 }}>
                    {ackConflict}
                  </Typography>
                </Alert>
              )}

              {convertError && (
                <Alert severity="error" sx={{ fontFamily: FONT_BODY, mb: 2 }}>
                  <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 600 }}>
                    Conversion failed
                  </AlertTitle>
                  {convertError}
                </Alert>
              )}

              {/* Acknowledgement names the numbers. A bare "are you sure?" would
                  be dismissed without ever reading them. */}
              <FormControlLabel
                sx={{ alignItems: 'flex-start', mr: 0 }}
                control={
                  <Checkbox
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    disabled={converting}
                    sx={{ mt: -0.5, color: CK.purple, '&.Mui-checked': { color: CK.purple } }}
                  />
                }
                label={
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.85rem', color: CK.navy }}>
                    I understand that converting <strong>{policyName}</strong> permanently changes
                    permissions in <strong>{plural(changed.length, 'account', 'accounts')}</strong>,
                    creates the policy for the first time in{' '}
                    <strong>{plural(newlyCreated.length, 'account', 'accounts')}</strong>, leaves{' '}
                    <strong>{plural(unchanged.length, 'account', 'accounts')}</strong> as they are,
                    and could not be predicted for{' '}
                    <strong>{plural(unknown.length, 'account', 'accounts')}</strong>.{' '}
                    <strong>{plural(impactAttached.length, 'permission set', 'permission sets')}</strong>{' '}
                    switch to the Prism-owned copy. This cannot be undone.
                  </Typography>
                }
              />

              <TextField
                fullWidth
                size="small"
                sx={{ mt: 2 }}
                label={`Type "${policyName}" to confirm`}
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                disabled={converting}
                InputProps={{ sx: { fontFamily: FONT_BODY } }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={closeConvert}
            disabled={converting}
            sx={{ textTransform: 'none', fontFamily: FONT_BODY }}
          >
            Cancel
          </Button>
          <ReadOnlyButton
            variant="contained"
            onClick={submitConversion}
            disabled={!canSubmitConversion}
            startIcon={converting ? <CircularProgress size={16} sx={{ color: '#FFF' }} /> : <SwapHorizIcon />}
            sx={{
              textTransform: 'none',
              fontFamily: FONT_BODY,
              fontWeight: 600,
              backgroundColor: CK.red,
              '&:hover': { backgroundColor: CK.redDark },
            }}
          >
            {converting
              ? 'Converting…'
              : changed.length > 0
                ? `Convert — change ${plural(changed.length, 'account', 'accounts')}`
                : 'Convert'}
          </ReadOnlyButton>
        </DialogActions>
      </Dialog>

      {/* ---------------------------------------------------------------- */}
      {/* remove dialog (detach only)                                       */}
      {/* ---------------------------------------------------------------- */}
      <Dialog
        open={removeOpen}
        onClose={() => !removing && setRemoveOpen(false)}
        {...dialogProps}
        disableEscapeKeyDown={removing}
      >
        <DialogTitle sx={dialogTitleSx}>Remove this reference</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.9rem', color: CK.navy, mb: 1.5 }}>
            Prism stops attaching <strong>{policyPath}{policyName}</strong> and drops it from{' '}
            <strong>{plural(attachedTo.length, 'permission set', 'permission sets')}</strong>. Users
            of those permission sets lose whatever it granted.
          </Typography>
          <Alert severity="info" sx={{ fontFamily: FONT_BODY }}>
            The customer&apos;s IAM policy itself is <strong>not</strong> deleted, in any account.
            Prism only removes its own pointer.
          </Alert>
          {removeError && (
            <Alert severity="error" sx={{ fontFamily: FONT_BODY, mt: 2 }}>
              {removeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setRemoveOpen(false)}
            disabled={removing}
            sx={{ textTransform: 'none', fontFamily: FONT_BODY }}
          >
            Cancel
          </Button>
          <ReadOnlyButton
            variant="contained"
            color="error"
            onClick={submitRemove}
            disabled={removing}
            startIcon={removing ? <CircularProgress size={16} sx={{ color: '#FFF' }} /> : <DeleteOutlineIcon />}
            sx={{ textTransform: 'none', fontFamily: FONT_BODY, fontWeight: 600 }}
          >
            {removing ? 'Removing…' : 'Remove reference'}
          </ReadOnlyButton>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}

export default PolicyReferenceDetail;
